import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { differenceInDays, format } from 'date-fns';

import { DatesRange } from '@utils/types/calendar';
import {
  EstimateUpsellAlternativeType,
  ListingPriceEstimateType,
} from '@utils/types/estimate';
import { getListingPriceRequest } from '@api/listings/estimateListingPrice';
import {
  calculateSuggestionNightlyDiscount,
  formatSuggestionPrice,
  showMonthsAndDays,
} from './helpers';
import { parseDateWithoutTimezone } from '@utils/dates';
import {
  calculateAverageTotalPerMonth,
  calculateAverageTotalPerNight,
} from '@utils/pricing';

import styles from './styles.module.scss';

const SUGGESTION_BOX_HEIGHT = '102px';

type Props = {
  listingId: string;
  selectedDates: DatesRange;
  changeUpsellAlternative: (value?: EstimateUpsellAlternativeType) => void;
  changeDates: (dates: DatesRange) => void;
  isInstantBooking: boolean;
  selectedUpsellAlternative?: EstimateUpsellAlternativeType;
  withPetFee?: boolean;
};

type YourSelectionProps = {
  amount: number;
  start: string;
  end: string;
  days: number;
};

export function SelectionWithSuggestions(props: Props) {
  const {
    listingId,
    selectedDates,
    selectedUpsellAlternative,
    changeUpsellAlternative,
    changeDates,
    withPetFee,
    isInstantBooking,
  } = props;
  const [yourSelection, setYourSelection] = useState<
    YourSelectionProps | undefined
  >();
  const [suggestions, setSuggestions] = useState<
    ListingPriceEstimateType['upsell_alternatives']
  >([]);

  const fetchSuggestions = async () => {
    const {
      original_start,
      original_end,
      original_estimate,
      upsell_alternatives,
    } = await getListingPriceRequest({
      listingId,
      dateStart: selectedUpsellAlternative?.id
        ? parseDateWithoutTimezone(selectedUpsellAlternative?.originalStart!)
        : selectedDates.from,
      dateEnd: selectedUpsellAlternative?.id
        ? parseDateWithoutTimezone(selectedUpsellAlternative?.originalEnd!)
        : selectedDates.to,
      upsellAlternative: selectedUpsellAlternative?.id,
      withPetFee,
    });

    setYourSelection({
      amount: original_estimate.total - original_estimate.taxes,
      start: original_start,
      end: original_end,
      days: differenceInDays(
        parseDateWithoutTimezone(original_end),
        parseDateWithoutTimezone(original_start)
      ),
    });

    setSuggestions(upsell_alternatives);
  };

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const onUpsellClick = (upsell: EstimateUpsellAlternativeType) => {
    changeUpsellAlternative({
      ...upsell,
      originalStart:
        selectedUpsellAlternative?.originalStart ||
        format(selectedDates.from!, 'yyyy-MM-dd'),
      originalEnd:
        selectedUpsellAlternative?.originalEnd ||
        format(selectedDates.to!, 'yyyy-MM-dd'),
    });

    changeDates({
      from: parseDateWithoutTimezone(upsell.start),
      to: parseDateWithoutTimezone(upsell.end),
    });
  };

  const onYourSelectionClick = () => {
    changeDates({
      from: parseDateWithoutTimezone(yourSelection?.start!),
      to: parseDateWithoutTimezone(yourSelection?.end!),
    });

    changeUpsellAlternative();
  };

  const { selectionMonthlyPrice, selectionNightlyPrice } = useMemo(() => {
    if (!yourSelection)
      return { selectionMonthlyPrice: 0, selectionNightlyPrice: 0 };

    return {
      selectionMonthlyPrice: calculateAverageTotalPerMonth(
        yourSelection.amount,
        yourSelection.days
      ),
      selectionNightlyPrice: calculateAverageTotalPerNight(
        yourSelection.amount,
        yourSelection.days
      ),
    };
  }, [yourSelection]);

  if (!yourSelection)
    return (
      <div className={styles.selectionWithSuggestions}>
        <div
          className="skeleton-box"
          style={{
            width: '100%',
            height: SUGGESTION_BOX_HEIGHT,
            borderRadius: '4px',
          }}
        />

        <div
          className="skeleton-box"
          style={{
            width: '100%',
            height: SUGGESTION_BOX_HEIGHT,
            borderRadius: '4px',
          }}
        />

        <div
          className="skeleton-box"
          style={{
            width: '100%',
            height: SUGGESTION_BOX_HEIGHT,
            borderRadius: '4px',
          }}
        />
      </div>
    );

  return (
    <div className={styles.selectionWithSuggestions}>
      <div
        className={styles.selectionWithSuggestions__block}
        onClick={onYourSelectionClick}
      >
        <div
          className={clsx([
            styles.selectionWithSuggestions__header,
            !selectedUpsellAlternative &&
              styles.selectionWithSuggestions__header__active,
          ])}
        >
          Your selection
        </div>

        {isInstantBooking ? (
          <div className={styles.selectionWithSuggestions__price}>
            <strong>{formatSuggestionPrice(yourSelection.amount)}</strong>
            <div>&nbsp;sub-total</div>
          </div>
        ) : (
          <div className={styles.selectionWithSuggestions__price}>
            <strong>{formatSuggestionPrice(selectionMonthlyPrice)}</strong>
            <div>&nbsp;per month</div>
          </div>
        )}

        {isInstantBooking ? (
          <div className={styles.selectionWithSuggestions__footer}>
            <div>{formatSuggestionPrice(selectionNightlyPrice)}</div>
            <div>
              {showMonthsAndDays(yourSelection.start, yourSelection.end)}
            </div>
          </div>
        ) : (
          <div
            className={styles.selectionWithSuggestions__footer}
            style={{ justifyContent: 'center' }}
          >
            <div>
              {showMonthsAndDays(yourSelection.start, yourSelection.end)}
            </div>
          </div>
        )}
      </div>

      {suggestions.slice(0, 2).map((s, key) => {
        const monthlyPrice = calculateAverageTotalPerMonth(
          s.estimate.total - s.estimate.taxes,
          s.estimate.days
        );
        const nightlyPrice = calculateAverageTotalPerNight(
          s.estimate.total - s.estimate.taxes,
          s.estimate.days
        );

        return (
          <div
            key={`$suggestion-${key}`}
            className={styles.selectionWithSuggestions__block}
            onClick={() => onUpsellClick(s)}
          >
            {isInstantBooking ? (
              <div
                className={clsx([
                  styles.selectionWithSuggestions__header,
                  selectedUpsellAlternative?.id === s.id &&
                    styles.selectionWithSuggestions__header__active,
                ])}
              >
                Save
                <strong>
                  &nbsp;
                  {formatSuggestionPrice(
                    calculateSuggestionNightlyDiscount(
                      s.estimate.discounts,
                      s.estimate.days
                    )
                  )}
                  /night
                </strong>
              </div>
            ) : (
              <div
                className={clsx([
                  styles.selectionWithSuggestions__header,
                  selectedUpsellAlternative?.id === s.id &&
                    styles.selectionWithSuggestions__header__active,
                ])}
              >
                Save
                <strong>
                  &nbsp;
                  {formatSuggestionPrice(selectionMonthlyPrice - monthlyPrice)}
                  /month
                </strong>
              </div>
            )}

            {isInstantBooking ? (
              <div className={styles.selectionWithSuggestions__price}>
                <strong>
                  {formatSuggestionPrice(s.estimate.total - s.estimate.taxes)}
                </strong>
                <div>&nbsp;sub-total</div>
              </div>
            ) : (
              <div className={styles.selectionWithSuggestions__price}>
                <strong>{formatSuggestionPrice(monthlyPrice)}</strong>
                <div>&nbsp;per month</div>
              </div>
            )}

            {isInstantBooking ? (
              <div className={styles.selectionWithSuggestions__footer}>
                <div>{formatSuggestionPrice(nightlyPrice)}</div>
                <div>{showMonthsAndDays(s.estimate.start, s.estimate.end)}</div>
              </div>
            ) : (
              <div
                className={styles.selectionWithSuggestions__footer}
                style={{ justifyContent: 'center' }}
              >
                <div>{showMonthsAndDays(s.estimate.start, s.estimate.end)}</div>
              </div>
            )}

            <div className={styles.selectionWithSuggestions__checkoutDate}>
              Checkout:{' '}
              <strong>
                {format(parseDateWithoutTimezone(s.end), 'MMMM do, yyyy')}
              </strong>
            </div>
          </div>
        );
      })}
    </div>
  );
}
